// app/src/modules/ui/Badge.tsx — the one way to render a status chip.
// Tones map to the design tokens so light/dark themes come free. Use `dot`
// for a leading status dot, `size="sm"` inside dense tables.
import React from "react";

export type BadgeTone = "neutral" | "info" | "success" | "warning" | "danger" | "accent";

const TONES: Record<BadgeTone, { bg: string; fg: string }> = {
  neutral: { bg: "var(--paper-2)", fg: "var(--ink-2)" },
  info: { bg: "var(--blue-soft)", fg: "var(--blue)" },
  success: { bg: "var(--green-soft)", fg: "var(--green)" },
  warning: { bg: "var(--gold-soft)", fg: "var(--gold)" },
  danger: { bg: "var(--accent-soft)", fg: "var(--accent-text, var(--accent))" },
  accent: { bg: "var(--accent)", fg: "var(--accent-ink)" },
};

export default function Badge({ tone = "neutral", size = "md", dot = false, style, children }: {
  tone?: BadgeTone; size?: "sm" | "md"; dot?: boolean; style?: React.CSSProperties; children: React.ReactNode;
}) {
  const t = TONES[tone];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: t.bg, color: t.fg, borderRadius: "var(--r-full)",
      padding: size === "sm" ? "2px 8px" : "3px 10px",
      /* 11 is the floor for anything a person reads. A small badge was 10.5,
         which put "completed" on every transaction row and the category on
         every expense line under it - 33 elements on two pages at 360px. */
      fontSize: size === "sm" ? 11 : 12, fontWeight: 800,
      letterSpacing: ".05em", textTransform: "uppercase", whiteSpace: "nowrap", ...style,
    }}>
      {dot && <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: "currentColor", flexShrink: 0 }} />}
      {children}
    </span>
  );
}
